import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  StyleSheet,
  View,
  FlatList,
  Pressable,
  TouchableOpacity,
  Platform,
  useWindowDimensions,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { ThemedText } from "@/components/ThemedText";
import { ThemedInput } from "@/components/ThemedInput";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";
import { ThemedDropDown } from "@/components/ThemedDropDown";
import { ThemedButton } from "@/components/ThemedButton";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Spacing, Fonts } from "@/constants/ThemeColours";
import { Ionicons } from "@expo/vector-icons";
import { ThemedViewSelector, DEFAULT_VIEW_MODES } from "@/components/ThemedViewSelector";
import { router, useLocalSearchParams } from "expo-router";
import { resolveListViewMode } from "@/utils/url-list-settings";

/**
 * Configuration option for sorting items in a ThemedList.
 */
export interface ISortOption<T> {
  /** Unique identifier for the sort option */
  key: string;
  /** Human-readable label displayed in the sort selector */
  label: string;
  /**
   * Function to extract a comparable primitive value from an item.
   * If not provided, the item's property matching `key` is used.
   */
  getValue?: (item: T) => string | number | boolean | Date | null | undefined;
  /**
   * Optional custom comparison function.
   */
  compareFn?: (a: T, b: T, direction: "asc" | "desc") => number;
}

/**
 * Configuration option for grouping items in a ThemedList.
 */
export interface IGroupOption<T> {
  /** Unique identifier for the group option */
  key: string;
  /** Human-readable label displayed in the group selector */
  label: string;
  /** Function to extract a group header title from an item */
  getGroupValue: (item: T) => string;
}

/**
 * Configuration option for custom dropdown filters in a ThemedList (e.g. Favourites rating).
 */
export interface ICustomFilterOption<T> {
  /** Unique identifier for the filter */
  key: string;
  /** Human-readable label displayed before the dropdown */
  label: string;
  /** Options available in the dropdown */
  options: { label: string; value: string }[];
  /** Default value (typically 'all') */
  defaultValue?: string;
  /** Predicate function evaluating if an item matches the selected filter value */
  predicate: (item: T, value: string) => boolean;
}

/**
 * Saved settings state for an instance of ThemedList.
 */
export interface IThemedListSettings {
  filterQuery?: string;
  sortKey?: string;
  sortDirection?: "asc" | "desc";
  groupKey?: string;
  customFilterValues?: Record<string, string>;
}

/**
 * View modes supported across all ThemedList instances.
 */
export type ThemedListViewMode =
  | "chip"
  | "compact"
  | "medium"
  | "full"
  | "custom";

export const THEMED_LIST_VIEW_MODES = DEFAULT_VIEW_MODES as {
  key: ThemedListViewMode;
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
}[];

/**
 * Props for the generic ThemedList component.
 */
export interface IThemedListProps<T> {
  /** The collection of items to display */
  data: T[];
  /** Render function for rendering an individual card item */
  renderItem: (item: T, index: number, isHovered: boolean, viewMode: ThemedListViewMode) => React.ReactNode;
  /** Function to derive a unique key for each item */
  keyExtractor?: (item: T, index: number) => string;
  /** Available sort options */
  sortOptions?: ISortOption<T>[];
  /** Initial sort key (defaults to first sortOption) */
  defaultSortKey?: string;
  /** Initial sort direction (default: "asc") */
  defaultSortDirection?: "asc" | "desc";
  /** Available grouping options */
  groupOptions?: IGroupOption<T>[];
  /** Initial grouping key (default: "none") */
  defaultGroupKey?: string;
  /** Unique identifier for persisting filter, sort, and grouping settings */
  persistenceKey?: string;
  /** Placeholder text for filter input */
  filterPlaceholder?: string;
  /** Extract searchable text from an item for filtering */
  getFilterableText?: (item: T) => string;
  /** Properties to check during filtering if getFilterableText is omitted */
  filterKeys?: (keyof T)[];
  /** Custom text to display when list is empty */
  emptyText?: string;
  /** Custom text to display when list has no items matching the active filter */
  emptyFilteredText?: string;
  /** Loading state flag */
  loading?: boolean;
  /** Custom style for the list content container */
  contentContainerStyle?: StyleProp<ViewStyle>;
  /** Optional custom header component above the list */
  headerComponent?: React.ReactNode;
  /** Optional custom footer component below the list */
  footerComponent?: React.ReactNode;
  /** Enable hover visual highlight on cards (default: true) */
  enableHoverEffect?: boolean;
  /** Callback triggered when a card is pressed */
  onItemPress?: (item: T) => void;
  /** Callback triggered when a card is long pressed (e.g. mobile batch selection) */
  onItemLongPress?: (item: T) => void;
  /** Callback triggered on right-click context menu (e.g. desktop/web batch selection) */
  onItemContextMenu?: (item: T, event?: any) => void;
  /** Whether to show the top toolbar (filter, group, and sort panels). Default: true */
  showToolbar?: boolean;
  /** Whether to show the segmented view mode selector (Details, Small, Medium, Large) in the toolbar (defaults to true) */
  showViewSelector?: boolean;
  /** Default view mode (defaults to "medium") */
  defaultViewMode?: ThemedListViewMode;
  /** Controlled view mode override */
  viewMode?: ThemedListViewMode;
  /** Callback fired when view mode changes */
  onViewModeChange?: (mode: ThemedListViewMode) => void;
  /** Optional pull-to-refresh callback (synchronous or asynchronous) */
  onRefresh?: (() => void) | (() => Promise<void>) | (() => Promise<any>);
  /** Optional pull-to-refresh refreshing state. If omitted, ThemedList automatically manages the boolean state during onRefresh execution */
  refreshing?: boolean;
  /** Optional custom action component rendered at the right side of the toolbar */
  toolbarRightComponent?: React.ReactNode;
  /** Optional "+ Add" button action or custom element displayed next to the sort order button */
  addButton?:
    | {
        title: string;
        onPress: () => void;
        variant?: "primary" | "secondary";
        testID?: string;
      }
    | React.ReactNode;
  /** Callback triggered when scroll reaches near the bottom for infinite scrolling */
  onEndReached?: () => void;
  /** Distance from end of list to trigger onEndReached (default: 0.5) */
  onEndReachedThreshold?: number;
  /** Number of items to display initially and add per page scroll for progressive infinite scrolling */
  pageSize?: number;
  /** Optional explicit column count (defaults to responsive based on viewMode) */
  columns?: number;
  /** Optional custom dropdown filter options (e.g. Favourites rating filter) */
  customFilters?: ICustomFilterOption<T>[];
  /** Explicit entity type (e.g. "media", "memory", "contact", etc.) */
  entityType?: "media" | "memory" | "contact" | "group" | "share" | "tag" | "user" | "message" | "activity" | string;
  /** Layout style for compact view mode. Defaults to "flow" when entityType is "media", and "columnar" otherwise. */
  compactLayout?: "columnar" | "flow";
  /** Whether to synchronize list settings (sort, order, group, view, q) with URL search parameters (defaults to true). */
  enableUrlSync?: boolean;
}

/**
 * Union item type for FlatList when supporting interleaved group headers.
 */
type FlatListItem<T> =
  | { type: "header"; id: string; title: string; count: number }
  | { type: "item"; id: string; item: T; originalIndex: number };

/**
 * A standard, modular list component for displaying cards (Memories, Media, Groups, Contacts, Shares)
 * with integrated top-left filtering, top-right grouping & sorting, card hover indication,
 * and cross-session settings persistence.
 */
export function ThemedList<T>({
  data,
  renderItem,
  keyExtractor,
  sortOptions,
  defaultSortKey,
  defaultSortDirection = "asc",
  groupOptions,
  defaultGroupKey = "none",
  persistenceKey,
  filterPlaceholder = "Filter items...",
  getFilterableText,
  filterKeys,
  emptyText = "No items found.",
  emptyFilteredText,
  loading = false,
  contentContainerStyle,
  headerComponent,
  footerComponent,
  enableHoverEffect = true,
  onItemPress,
  onItemLongPress,
  onItemContextMenu,
  showToolbar = true,
  showViewSelector = true,
  defaultViewMode = "medium",
  viewMode,
  onViewModeChange,
  onRefresh,
  refreshing,
  toolbarRightComponent,
  addButton,
  onEndReached,
  onEndReachedThreshold = 0.5,
  pageSize,
  columns,
  customFilters,
  entityType,
  compactLayout,
  enableUrlSync = true,
}: IThemedListProps<T>) {
  const cardBg = useThemeColor({}, "card");
  const screenBgColor = useThemeColor({}, "background");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const textMuted = useThemeColor({}, "textMuted");
  const badgeBg = useThemeColor({}, "buttonSecondary");

  const routeParams = useLocalSearchParams<{
    sort?: string;
    order?: string;
    group?: string;
    view?: string;
    viewMode?: string;
    q?: string;
  }>();

  // Resolve initial URL parameters
  const initialUrlView = enableUrlSync ? resolveListViewMode(routeParams.view || routeParams.viewMode) : undefined;
  const initialUrlSort =
    enableUrlSync && routeParams.sort && sortOptions?.some((o) => o.key === routeParams.sort)
      ? routeParams.sort
      : undefined;
  const initialUrlOrder =
    enableUrlSync && (routeParams.order === "asc" || routeParams.order === "desc")
      ? (routeParams.order as "asc" | "desc")
      : undefined;
  const initialUrlGroup =
    enableUrlSync &&
    routeParams.group &&
    (routeParams.group === "none" || groupOptions?.some((g) => g.key === routeParams.group))
      ? routeParams.group
      : undefined;
  const initialUrlQ = enableUrlSync && typeof routeParams.q === "string" ? routeParams.q : undefined;

  const [internalViewMode, setInternalViewMode] = useState<ThemedListViewMode>(
    initialUrlView || defaultViewMode
  );
  const activeViewMode = viewMode !== undefined ? viewMode : internalViewMode;

  useEffect(() => {
    if (!persistenceKey || initialUrlView !== undefined) return;
    AsyncStorage.getItem(`${persistenceKey}_view_mode`)
      .then((saved) => {
        let resolved = saved as ThemedListViewMode | undefined;
        // Migrate legacy stored settings to canonical 5 card tiers
        if (saved === "details" || saved === "large") resolved = "full";
        else if (saved === "small") resolved = "compact";

        if (
          resolved === "chip" ||
          resolved === "compact" ||
          resolved === "medium" ||
          resolved === "full" ||
          resolved === "custom"
        ) {
          setInternalViewMode(resolved);
          onViewModeChange?.(resolved);
        }
      })
      .catch(() => {});
  }, [persistenceKey, initialUrlView]);

  const handleViewModeChange = (mode: ThemedListViewMode) => {
    setInternalViewMode(mode);
    onViewModeChange?.(mode);
    if (persistenceKey) {
      AsyncStorage.setItem(`${persistenceKey}_view_mode`, mode).catch(() => {});
    }
  };

  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < 680;
  const isFlowChip = activeViewMode === "chip";

  const isFlowCompact =
    activeViewMode === "compact" &&
    (compactLayout === "flow" || (compactLayout === undefined && entityType === "media"));

  const isFlow = isFlowChip || isFlowCompact;

  const responsiveColumns = useMemo(() => {
    if (activeViewMode === "chip") {
      return 1;
    }
    if (activeViewMode === "compact") {
      if (windowWidth >= 1700) return 6;
      if (windowWidth >= 1400) return 5;
      if (windowWidth >= 1100) return 4;
      if (windowWidth >= 800) return 3;
      if (windowWidth >= 550) return 2;
      return 1;
    }
    if (activeViewMode === "full") {
      return 1;
    }
    if (activeViewMode === "custom") {
      if (windowWidth >= 1400) return 3;
      if (windowWidth >= 800) return 2;
      return 1;
    }
    // medium (default Tier 3):
    if (windowWidth >= 1200) return 4;
    if (windowWidth >= 900) return 3;
    if (windowWidth >= 600) return 2;
    return 1;
  }, [activeViewMode, windowWidth]);

  const activeColumns = columns !== undefined ? columns : responsiveColumns;

  const nativeFlowColumns = useMemo(() => {
    // 90px card + 3px margins
    return Math.max(1, Math.floor(windowWidth / 93));
  }, [windowWidth]);

  const webGridStyle = useMemo(() => {
    if (Platform.OS !== "web") return null;
    if (isFlowChip) {
      return {
        display: "flex" as any,
        flexDirection: "row" as any,
        flexWrap: "wrap" as any,
        gap: 6,
        alignItems: "center" as any,
      };
    }
    if (activeViewMode === "compact") {
      if (isFlowCompact) {
        return {
          display: "flex" as any,
          flexDirection: "row" as any,
          flexWrap: "wrap" as any,
          gap: 3,
          alignItems: "flex-start",
        };
      }
      if (activeColumns <= 1) return null;
      return {
        display: "grid" as any,
        gridTemplateColumns:
          columns !== undefined
            ? `repeat(${columns}, minmax(0, 1fr))`
            : ("repeat(auto-fill, minmax(260px, 1fr))" as any),
      };
    }
    if (activeColumns <= 1) return null;
    return {
      display: "grid" as any,
      gridTemplateColumns: `repeat(${activeColumns}, minmax(0, 1fr))` as any,
    };
  }, [activeColumns, activeViewMode, columns, isFlowCompact, isFlowChip]);

  const itemWrapperResponsiveStyle = useMemo(() => {
    if (isFlow) {
      return {
        width: "auto" as any,
        flex: undefined,
      };
    }
    if (activeColumns > 1) {
      if (Platform.OS === "web") {
        return {
          width: "100%",
          boxSizing: "border-box" as any,
        };
      }
      return {
        flex: 1,
        width: "100%",
      };
    }
    return {
      width: "100%",
    };
  }, [activeColumns, isFlow]);

  const CellRenderer = useCallback(
    ({ item, style, children, ...props }: any) => {
      if (item?.type === "header") {
        const headerCell = (activeColumns > 1 || isFlow)
          ? styles.fullWidthGridCell
          : styles.singleColumnCell;
        return (
          <View style={[style, headerCell]} {...props}>
            {children}
          </View>
        );
      }
      if (isFlow) {
        return (
          <View style={[style, { width: "auto", display: "inline-flex" as any }]} {...props}>
            {children}
          </View>
        );
      }
      if (activeColumns <= 1) {
        return (
          <View style={[style, styles.singleColumnCell]} {...props}>
            {children}
          </View>
        );
      }
      return (
        <View style={[style, itemWrapperResponsiveStyle]} {...props}>
          {children}
        </View>
      );
    },
    [activeColumns, itemWrapperResponsiveStyle, isFlow],
  );

  const [filterQuery, setFilterQuery] = useState(initialUrlQ !== undefined ? initialUrlQ : "");
  const [activeSortKey, setActiveSortKey] = useState<string>(
    initialUrlSort || defaultSortKey || (sortOptions && sortOptions.length > 0 ? sortOptions[0].key : "")
  );
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    initialUrlOrder || defaultSortDirection
  );
  const [activeGroupKey, setActiveGroupKey] = useState<string>(
    initialUrlGroup || defaultGroupKey
  );
  const [activeCustomFilterValues, setActiveCustomFilterValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    customFilters?.forEach((f) => {
      initial[f.key] = f.defaultValue || "all";
    });
    return initial;
  });

  // Controlled or automatic internal pull-to-refresh state
  const [internalRefreshing, setInternalRefreshing] = useState(false);
  const isRefreshing = refreshing !== undefined ? Boolean(refreshing) : internalRefreshing;

  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const refreshingPropRef = useRef(refreshing);
  refreshingPropRef.current = refreshing;

  const handleRefresh = useCallback(async () => {
    const fn = onRefreshRef.current;
    if (!fn) return;
    if (refreshingPropRef.current === undefined) {
      setInternalRefreshing(true);
    }
    try {
      await fn();
    } finally {
      if (refreshingPropRef.current === undefined) {
        setInternalRefreshing(false);
      }
    }
  }, []);

  const isCustomFilterApplied = useMemo(() => {
    if (!customFilters || customFilters.length === 0) return false;
    return customFilters.some((f) => {
      const val = activeCustomFilterValues[f.key] ?? f.defaultValue ?? "all";
      return val !== "all";
    });
  }, [customFilters, activeCustomFilterValues]);

  const isFilterApplied = filterQuery.trim().length > 0 || isCustomFilterApplied;

  const displayEmptyText = useMemo(() => {
    if (isFilterApplied) {
      if (emptyFilteredText) return emptyFilteredText;
      const match = emptyText.match(/No\s+(.*?)\s+found/i);
      if (match) {
        const noun = match[1].trim();
        const hasTrailingDot = emptyText.trim().endsWith(".");
        return `No ${noun} found matching the filters${hasTrailingDot ? "." : ""}`;
      }
      return `${emptyText.replace(/\.?$/, "")} matching the filters.`;
    }
    return emptyText;
  }, [isFilterApplied, emptyText, emptyFilteredText]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [visibleCount, setVisibleCount] = useState<number>(pageSize || Number.MAX_SAFE_INTEGER);

  useEffect(() => {
    if (pageSize) {
      setVisibleCount(pageSize);
    }
  }, [pageSize, filterQuery, activeSortKey, sortDirection, activeGroupKey, activeCustomFilterValues]);

  const isHydratedRef = useRef(false);
  const isInternalUrlUpdateRef = useRef(false);

  // Restore settings from AsyncStorage on mount if persistenceKey is provided
  useEffect(() => {
    if (!persistenceKey) {
      isHydratedRef.current = true;
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        const stored = await AsyncStorage.getItem(`@themed_list_settings_${persistenceKey}`);
        if (stored && isMounted) {
          const parsed: IThemedListSettings = JSON.parse(stored);
          if (parsed.filterQuery !== undefined && (!enableUrlSync || routeParams.q === undefined)) {
            setFilterQuery(parsed.filterQuery);
          }
          if (
            parsed.sortKey !== undefined &&
            (!enableUrlSync || routeParams.sort === undefined) &&
            sortOptions?.some((o) => o.key === parsed.sortKey)
          ) {
            setActiveSortKey(parsed.sortKey);
          }
          if (
            (parsed.sortDirection === "asc" || parsed.sortDirection === "desc") &&
            (!enableUrlSync || routeParams.order === undefined)
          ) {
            setSortDirection(parsed.sortDirection);
          }
          if (
            parsed.groupKey !== undefined &&
            (!enableUrlSync || routeParams.group === undefined) &&
            (parsed.groupKey === "none" || groupOptions?.some((g) => g.key === parsed.groupKey))
          ) {
            setActiveGroupKey(parsed.groupKey);
          }
          if (parsed.customFilterValues && typeof parsed.customFilterValues === "object") {
            setActiveCustomFilterValues((prev) => ({
              ...prev,
              ...parsed.customFilterValues,
            }));
          }
        }
      } catch (err) {
        console.warn(`[ThemedList] Failed to load settings for ${persistenceKey}:`, err);
      } finally {
        if (isMounted) {
          isHydratedRef.current = true;
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [persistenceKey]);

  // Persist settings whenever filterQuery, activeSortKey, sortDirection, activeGroupKey or activeCustomFilterValues changes
  useEffect(() => {
    if (!persistenceKey || !isHydratedRef.current) return;

    const settings: IThemedListSettings = {
      filterQuery,
      sortKey: activeSortKey,
      sortDirection,
      groupKey: activeGroupKey,
      customFilterValues: activeCustomFilterValues,
    };

    const timer = setTimeout(() => {
      AsyncStorage.setItem(`@themed_list_settings_${persistenceKey}`, JSON.stringify(settings)).catch((err) => {
        console.warn(`[ThemedList] Failed to save settings for ${persistenceKey}:`, err);
      });
    }, 250);

    return () => clearTimeout(timer);
  }, [persistenceKey, filterQuery, activeSortKey, sortDirection, activeGroupKey, activeCustomFilterValues]);

  // Sync to URL parameters when sort, order, group, viewMode, or query changes
  useEffect(() => {
    if (!enableUrlSync || !isHydratedRef.current) return;

    const timer = setTimeout(() => {
      isInternalUrlUpdateRef.current = true;
      router.setParams({
        sort: activeSortKey,
        order: sortDirection,
        group: activeGroupKey !== "none" ? activeGroupKey : undefined,
        view: activeViewMode,
        q: filterQuery.trim().length > 0 ? filterQuery.trim() : undefined,
      });
    }, 250);

    return () => clearTimeout(timer);
  }, [enableUrlSync, activeSortKey, sortDirection, activeGroupKey, activeViewMode, filterQuery]);

  // Synchronize with external URL parameter changes (e.g. browser back/forward)
  useEffect(() => {
    if (!enableUrlSync) return;

    if (isInternalUrlUpdateRef.current) {
      isInternalUrlUpdateRef.current = false;
      return;
    }

    if (
      routeParams.sort &&
      sortOptions?.some((o) => o.key === routeParams.sort) &&
      routeParams.sort !== activeSortKey
    ) {
      setActiveSortKey(routeParams.sort);
    }
    if (
      (routeParams.order === "asc" || routeParams.order === "desc") &&
      routeParams.order !== sortDirection
    ) {
      setSortDirection(routeParams.order as "asc" | "desc");
    }
    if (
      routeParams.group &&
      (routeParams.group === "none" || groupOptions?.some((g) => g.key === routeParams.group)) &&
      routeParams.group !== activeGroupKey
    ) {
      setActiveGroupKey(routeParams.group);
    }
    const resolvedView = resolveListViewMode(routeParams.view || routeParams.viewMode);
    if (resolvedView && resolvedView !== activeViewMode) {
      setInternalViewMode(resolvedView);
      onViewModeChange?.(resolvedView);
    }
    if (routeParams.q !== undefined && routeParams.q !== filterQuery) {
      setFilterQuery(routeParams.q);
    }
  }, [
    enableUrlSync,
    routeParams.sort,
    routeParams.order,
    routeParams.group,
    routeParams.view,
    routeParams.viewMode,
    routeParams.q,
  ]);

  // Active sort option object
  const activeSortOption = useMemo(() => {
    return sortOptions?.find((opt) => opt.key === activeSortKey) || sortOptions?.[0];
  }, [sortOptions, activeSortKey]);

  // Active group option object
  const activeGroupOption = useMemo(() => {
    if (activeGroupKey === "none") return null;
    return groupOptions?.find((opt) => opt.key === activeGroupKey) || null;
  }, [groupOptions, activeGroupKey]);

  // Filtered and Sorted list computation
  const processedData = useMemo(() => {
    let result = [...data];

    // 1. Text Filter
    const query = filterQuery.trim().toLowerCase();
    if (query) {
      result = result.filter((item) => {
        if (getFilterableText) {
          const itemText = getFilterableText(item);
          return itemText ? itemText.toLowerCase().includes(query) : false;
        }

        if (filterKeys && filterKeys.length > 0) {
          return filterKeys.some((k) => {
            const val = (item as any)[k];
            if (val === null || val === undefined) return false;
            if (typeof val === "object") {
              if (typeof val.text === "string" && val.text.toLowerCase().includes(query)) return true;
              return Object.values(val).some(
                (v) => (typeof v === "string" || typeof v === "number") && String(v).toLowerCase().includes(query)
              );
            }
            return String(val).toLowerCase().includes(query);
          });
        }

        const values = Object.values(item as any);
        return values.some((val) => {
          if (typeof val === "string" || typeof val === "number") {
            return String(val).toLowerCase().includes(query);
          }
          if (val && typeof val === "object") {
            return Object.values(val).some(
              (v) => (typeof v === "string" || typeof v === "number") && String(v).toLowerCase().includes(query)
            );
          }
          return false;
        });
      });
    }

    // 1.5 Custom Filters (e.g. Favourites Rating)
    if (customFilters && customFilters.length > 0) {
      for (const filter of customFilters) {
        const activeVal = activeCustomFilterValues[filter.key] ?? filter.defaultValue ?? "all";
        if (activeVal !== "all") {
          result = result.filter((item) => filter.predicate(item, activeVal));
        }
      }
    }

    // 2. Attribute Sorting
    if (activeSortOption) {
      result.sort((a, b) => {
        if (activeSortOption.compareFn) {
          return activeSortOption.compareFn(a, b, sortDirection);
        }

        let valA: any = activeSortOption.getValue
          ? activeSortOption.getValue(a)
          : (a as any)[activeSortOption.key];
        let valB: any = activeSortOption.getValue
          ? activeSortOption.getValue(b)
          : (b as any)[activeSortOption.key];

        if (valA === undefined || valA === null) valA = "";
        if (valB === undefined || valB === null) valB = "";

        if (valA instanceof Date) valA = valA.getTime();
        if (valB instanceof Date) valB = valB.getTime();

        let comparison = 0;
        if (typeof valA === "string" && typeof valB === "string") {
          comparison = valA.localeCompare(valB, undefined, { numeric: true, sensitivity: "base" });
        } else if (typeof valA === "number" && typeof valB === "number") {
          comparison = valA - valB;
        } else {
          comparison = String(valA).localeCompare(String(valB));
        }

        return sortDirection === "asc" ? comparison : -comparison;
      });
    }

    return result;
  }, [data, filterQuery, getFilterableText, filterKeys, customFilters, activeCustomFilterValues, activeSortOption, sortDirection]);

  // Derived unique key extractor
  const defaultKeyExtractor = (item: T, index: number): string => {
    if (keyExtractor) return keyExtractor(item, index);
    if ((item as any)?.id) return String((item as any).id);
    if ((item as any)?.key) return String((item as any).key);
    return `item-${index}`;
  };

  // Flattened data including headers when grouped
  const flattenedListItems = useMemo((): FlatListItem<T>[] => {
    if (!activeGroupOption || activeGroupKey === "none") {
      return processedData.map((item, idx) => ({
        type: "item",
        id: defaultKeyExtractor(item, idx),
        item,
        originalIndex: idx,
      }));
    }

    const groupsMap = new Map<string, T[]>();
    for (const item of processedData) {
      const gVal = activeGroupOption.getGroupValue(item) || "Other";
      if (!groupsMap.has(gVal)) {
        groupsMap.set(gVal, []);
      }
      groupsMap.get(gVal)!.push(item);
    }

    const items: FlatListItem<T>[] = [];
    let itemIdx = 0;
    for (const [groupTitle, groupItems] of groupsMap.entries()) {
      items.push({
        type: "header",
        id: `group-header-${groupTitle}`,
        title: groupTitle,
        count: groupItems.length,
      });

      for (const item of groupItems) {
        items.push({
          type: "item",
          id: defaultKeyExtractor(item, itemIdx),
          item,
          originalIndex: itemIdx++,
        });
      }
    }

    return items;
  }, [processedData, activeGroupOption, activeGroupKey, keyExtractor]);

  const displayedListItems = useMemo(() => {
    if (!pageSize) return flattenedListItems;
    return flattenedListItems.slice(0, visibleCount);
  }, [flattenedListItems, pageSize, visibleCount]);

  const handleEndReached = () => {
    if (pageSize && visibleCount < flattenedListItems.length) {
      setVisibleCount((prev) => Math.min(prev + pageSize, flattenedListItems.length));
    }
    onEndReached?.();
  };

  const toggleSortDirection = () => {
    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const renderViewSelector = () => (
    <ThemedViewSelector<ThemedListViewMode>
      activeMode={activeViewMode}
      onModeChange={handleViewModeChange}
      options={THEMED_LIST_VIEW_MODES}
    />
  );

  return (
    <View style={styles.wrapper}>
      {/* Top Toolbar */}
      {showToolbar && (
        <View
          style={[
            styles.toolbarContainer,
            isMobile && styles.toolbarContainerMobile,
            { backgroundColor: screenBgColor, borderBottomColor: borderColor },
          ]}
        >
          {/* Top Left: Filter Panel */}
          <View style={[styles.filterPanel, isMobile && styles.filterPanelMobile]}>
            <View style={styles.filterInputWrapper}>
              <Ionicons name="search-outline" size={16} color={textMuted} style={styles.searchIcon} />
              <ThemedInput
                placeholder={filterPlaceholder}
                value={filterQuery}
                onChangeText={setFilterQuery}
                style={styles.filterInput}
              />
              {filterQuery.length > 0 && (
                <TouchableOpacity
                  onPress={() => setFilterQuery("")}
                  style={styles.clearBtn}
                  activeOpacity={0.7}
                  accessibilityLabel="Clear filter"
                >
                  <Ionicons name="close-circle" size={16} color={textMuted} />
                </TouchableOpacity>
              )}
            </View>
            {(filterQuery.length > 0 || isCustomFilterApplied) && (
              <ThemedText style={[styles.filterCountBadge, { color: textMuted }]}>
                {processedData.length} of {data.length}
              </ThemedText>
            )}
          </View>

          {/* Top Right: Actions (Custom Filters, Group By & Sort Panels) */}
          <View style={[styles.actionsPanel, isMobile && styles.actionsPanelMobile]}>
            {/* Custom Filters (e.g. Favourites Rating) */}
            {customFilters &&
              customFilters.map((filter) => {
                const currentVal = activeCustomFilterValues[filter.key] ?? filter.defaultValue ?? "all";
                return (
                  <View key={filter.key} style={styles.groupPanel}>
                    {filter.label ? (
                      <ThemedText style={[styles.toolbarLabel, { color: textMuted }]}>
                        {filter.label}
                      </ThemedText>
                    ) : null}
                    <ThemedDropDown
                      compact
                      options={filter.options}
                      value={currentVal}
                      onSelect={(opt) => {
                        setActiveCustomFilterValues((prev) => ({
                          ...prev,
                          [filter.key]: opt.value,
                        }));
                      }}
                      containerStyle={{ marginVertical: 0 }}
                      triggerStyle={{ minWidth: 100 }}
                      accessibilityLabel={filter.label || filter.key}
                    />
                  </View>
                );
              })}

            {/* Group By Panel with Standardised ThemedDropDown */}
            {groupOptions && groupOptions.length > 0 && (
              <View style={[styles.groupPanel, isMobile && styles.controlItemMobile]}>
                <ThemedText style={[styles.toolbarLabel, { color: textMuted }]}>Group:</ThemedText>
                <ThemedDropDown
                  compact
                  options={[
                    { label: "None (Flat List)", value: "none" },
                    ...groupOptions.map((option) => ({
                      label: option.label,
                      value: option.key,
                    })),
                  ]}
                  value={activeGroupKey}
                  onSelect={(opt) => setActiveGroupKey(opt.value)}
                  containerStyle={{ marginVertical: 0, flexShrink: 1 }}
                  triggerStyle={{ minWidth: isMobile ? 85 : 100 }}
                  accessibilityLabel="Group cards"
                />
              </View>
            )}

            {/* Sort Panel with Standardised ThemedDropDown & Direction Toggle */}
            <View style={[styles.sortPanel, isMobile && styles.controlItemMobile]}>
              {sortOptions && sortOptions.length > 0 && (
                <>
                  <ThemedText style={[styles.toolbarLabel, { color: textMuted }]}>Sort:</ThemedText>
                  <ThemedDropDown
                    compact
                    options={sortOptions.map((option) => ({
                      label: option.label,
                      value: option.key,
                    }))}
                    value={activeSortKey}
                    onSelect={(opt) => setActiveSortKey(opt.value)}
                    containerStyle={{ marginVertical: 0 }}
                    triggerStyle={{ minWidth: 110 }}
                    accessibilityLabel="Sort cards"
                  />

                  {/* Sort Direction Toggle Button */}
                  <TouchableOpacity
                    style={[styles.sortDirectionBtn, { backgroundColor: badgeBg, borderColor }]}
                    onPress={toggleSortDirection}
                    activeOpacity={0.7}
                    accessibilityLabel={`Sort direction: ${sortDirection === "asc" ? "Ascending" : "Descending"}`}
                  >
                    <Ionicons
                      name={sortDirection === "asc" ? "arrow-up" : "arrow-down"}
                      size={15}
                      color={tintColor}
                    />
                  </TouchableOpacity>
                </>
              )}

              {/* Action / Add button placed directly next to the sort order button */}
              {addButton ? (
                React.isValidElement(addButton) ? (
                  addButton
                ) : (
                  <ThemedButton
                    title={(addButton as any).title}
                    variant={(addButton as any).variant ?? "primary"}
                    onPress={(addButton as any).onPress}
                    testID={(addButton as any).testID}
                    style={styles.toolbarAddBtn}
                  />
                )
              ) : null}
            </View>

            {/* Segmented View Mode Selector */}
            {showViewSelector && renderViewSelector()}

            {/* Custom Toolbar Right Component */}
            {toolbarRightComponent}
          </View>
        </View>
      )}

      {/* Floating progress indicator hovering over the list (zero height / zero CLS) */}
      <View style={styles.listContainer}>
        <ThemedProgressBar loading={loading} overlay={true} />

        {/* List Content */}
        <FlatList<FlatListItem<T>>
        key={`themed-list-${isFlowChip ? "chip-flow" : isFlowCompact ? "flow-" + nativeFlowColumns : "cols-" + activeColumns}`}
        style={styles.flatList}
        data={displayedListItems}
        extraData={data}
        keyExtractor={(entry) => entry.id}
        numColumns={
          isFlowChip
            ? undefined
            : isFlowCompact
              ? (Platform.OS !== "web" && (!activeGroupOption || activeGroupKey === "none") ? nativeFlowColumns : undefined)
              : (Platform.OS !== "web" && activeColumns > 1 && (!activeGroupOption || activeGroupKey === "none") ? activeColumns : undefined)
        }
        columnWrapperStyle={
          isFlowChip
            ? undefined
            : isFlowCompact
              ? (Platform.OS !== "web" && (!activeGroupOption || activeGroupKey === "none") ? styles.flowColumnWrapper : undefined)
              : (Platform.OS !== "web" && activeColumns > 1 && (!activeGroupOption || activeGroupKey === "none") ? styles.columnWrapper : undefined)
        }
        contentContainerStyle={[
          styles.listContent,
          isFlowChip
            ? styles.chipFlowListContent
            : isFlowCompact
              ? styles.flowListContent
              : (displayedListItems.length > 0 && activeColumns > 1 && styles.gridListContent),
          isFlow
            ? (Platform.OS === "web" ? webGridStyle : undefined)
            : (displayedListItems.length > 0 ? webGridStyle : styles.singleColumnCell),
          contentContainerStyle,
        ]}
        onRefresh={onRefresh ? handleRefresh : undefined}
        refreshing={onRefresh ? isRefreshing : undefined}
        onEndReached={handleEndReached}
        onEndReachedThreshold={onEndReachedThreshold ?? 0.5}
        initialNumToRender={12}
        maxToRenderPerBatch={12}
        windowSize={5}
        removeClippedSubviews={Platform.OS !== "web"}
        CellRendererComponent={Platform.OS === "web" ? CellRenderer : undefined}
        ListHeaderComponentStyle={
          Platform.OS === "web"
            ? (activeColumns > 1 || isFlow)
              ? styles.fullWidthGridCell
              : styles.singleColumnCell
            : undefined
        }
        ListFooterComponentStyle={
          Platform.OS === "web"
            ? (activeColumns > 1 || isFlow)
              ? styles.fullWidthGridCell
              : styles.singleColumnCell
            : undefined
        }
        {...({
          ListEmptyComponentStyle:
            Platform.OS === "web"
              ? styles.fullWidthGridCell
              : { width: "100%" },
        } as any)}
        ListHeaderComponent={
          headerComponent ? (
            <View style={(activeColumns > 1 || isFlow) ? styles.fullWidthGridCell : styles.singleColumnCell}>
              {headerComponent}
            </View>
          ) : undefined
        }
        ListFooterComponent={
          <View style={(activeColumns > 1 || isFlow) ? styles.fullWidthGridCell : styles.singleColumnCell}>
            {footerComponent ? <>{footerComponent}</> : null}
            {pageSize !== undefined && visibleCount < flattenedListItems.length && (
              <View style={styles.paginationFooter}>
                <ThemedText style={styles.paginationText}>
                  Showing {Math.min(visibleCount, flattenedListItems.length)} of {flattenedListItems.length} items (scroll for more)
                </ThemedText>
              </View>
            )}
          </View>
        }
        ListEmptyComponent={
          !loading ? (
            <View style={[styles.emptyContainer, { borderColor, backgroundColor: badgeBg + "40" }]}>
              <Ionicons name="file-tray-outline" size={36} color={tintColor} style={{ marginBottom: 8, opacity: 0.7 }} />
              <ThemedText type="stamp" style={[styles.emptyArchivalLabel, { color: tintColor }]}>
                Archival Ledger
              </ThemedText>
              <ThemedText style={[styles.emptyText, { color: textMuted }]}>{displayEmptyText}</ThemedText>
              {isFilterApplied && (
                <TouchableOpacity
                  onPress={() => setFilterQuery("")}
                  style={styles.clearFilterBtn}
                  activeOpacity={0.7}
                >
                  <ThemedText style={[styles.clearFilterText, { color: tintColor }]}>
                    Clear Filter
                  </ThemedText>
                </TouchableOpacity>
              )}
            </View>
          ) : null
        }
        renderItem={({ item: entry }) => {
          if (entry.type === "header") {
            return (
              <View style={[styles.groupHeaderContainer, { backgroundColor: badgeBg, borderColor }]}>
                <View style={styles.groupHeaderLeft}>
                  <Ionicons name="folder-open-outline" size={16} color={tintColor} />
                  <ThemedText style={styles.groupHeaderTitle}>
                    {entry.title}
                  </ThemedText>
                </View>
                <View style={[styles.groupHeaderBadge, { backgroundColor: cardBg, borderColor }]}>
                  <ThemedText type="stamp" style={[styles.groupHeaderBadgeText, { color: tintColor }]}>
                    {entry.count} {entry.count === 1 ? "record" : "records"}
                  </ThemedText>
                </View>
              </View>
            );
          }

          const isHovered = hoveredIndex === entry.originalIndex;

          return (
            <Pressable
              onPress={onItemPress ? () => onItemPress(entry.item) : undefined}
              onLongPress={onItemLongPress ? () => onItemLongPress(entry.item) : undefined}
              delayLongPress={350}
              style={[
                styles.itemWrapper,
                isFlow
                  ? styles.flowItemWrapper
                  : (activeColumns > 1 && { flex: 1 }),
                Platform.OS !== "web" && !isFlow && itemWrapperResponsiveStyle,
                (onItemPress || Platform.OS === "web") && ({
                  cursor: onItemPress ? "pointer" : "default",
                } as any),
              ]}
              onHoverIn={() => setHoveredIndex(entry.originalIndex)}
              onHoverOut={() => setHoveredIndex(null)}
              {...(Platform.OS === "web"
                ? {
                    onMouseEnter: () => setHoveredIndex(entry.originalIndex),
                    onMouseLeave: () => setHoveredIndex(null),
                    onPointerEnter: () => setHoveredIndex(entry.originalIndex),
                    onPointerLeave: () => setHoveredIndex(null),
                    onContextMenu: (e: any) => {
                      if (onItemContextMenu) {
                        e.preventDefault();
                        onItemContextMenu(entry.item, e);
                      }
                    },
                  }
                : {})}
            >
              {renderItem(entry.item, entry.originalIndex, isHovered, activeViewMode)}
            </Pressable>
          );
        }}
      />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    minHeight: 0,
    width: "100%",
  },
  listContainer: {
    flex: 1,
    minHeight: 0,
    position: "relative",
    width: "100%",
  },
  toolbarContainer: {
    position: "sticky" as any,
    top: 0,
    zIndex: 20,
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: Spacing.two,
    paddingVertical: Spacing.one,
    paddingBottom: Spacing.two,
    marginBottom: Spacing.one,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  toolbarContainerMobile: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: Spacing.one,
    paddingBottom: Spacing.one,
  },
  flatList: {
    flex: 1,
    minHeight: 0,
    width: "100%",
  },
  filterPanel: {
    flex: 1,
    minWidth: 200,
    maxWidth: 360,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.one,
  },
  filterPanelMobile: {
    minWidth: "100%",
    maxWidth: "100%",
    width: "100%",
    flex: undefined,
  },
  filterInputWrapper: {
    flex: 1,
    position: "relative",
    justifyContent: "center",
  },
  searchIcon: {
    position: "absolute",
    left: 10,
    zIndex: 2,
  },
  filterInput: {
    paddingLeft: 34,
    paddingRight: 28,
    marginVertical: 0,
    height: 36,
    borderRadius: 0,
    fontSize: 13,
  },
  clearBtn: {
    position: "absolute",
    right: 8,
    zIndex: 2,
    padding: 2,
  },
  filterCountBadge: {
    fontSize: 11,
    fontWeight: "500",
  },
  actionsPanel: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: Spacing.two,
    justifyContent: "flex-end",
    zIndex: 20,
  },
  actionsPanelMobile: {
    width: "100%",
    justifyContent: "flex-start",
    gap: Spacing.one,
  },
  groupPanel: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.one,
  },
  controlItemMobile: {
    flexShrink: 1,
    justifyContent: "flex-start",
  },
  dropdownContainer: {
    position: "relative",
  },
  dropdownBackdrop: {
    ...Platform.select({
      web: {
        position: "fixed" as any,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      },
      default: {
        position: "absolute",
        top: -1000,
        left: -1000,
        right: -1000,
        bottom: -1000,
      },
    }),
    zIndex: 998,
  },
  dropdownMenu: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: 4,
    minWidth: 160,
    borderRadius: 0,
    borderWidth: 1,
    paddingVertical: 4,
    paddingHorizontal: 4,
    zIndex: 999,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
  },
  dropdownOptionItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 0,
  },
  dropdownOptionText: {
    fontSize: 12,
  },
  sortPanel: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.one,
    justifyContent: "flex-end",
  },
  toolbarLabel: {
    fontFamily: Fonts.mono,
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.8,
    textTransform: "uppercase",
  },
  sortDirectionBtn: {
    width: 30,
    height: 30,
    borderRadius: 0,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  toolbarAddBtn: {
    marginVertical: 0,
    paddingVertical: 4,
    paddingHorizontal: 10,
  },
  viewSelectorContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 0,
    borderWidth: 1,
    padding: 2,
    gap: 2,
  },
  viewModeBtn: {
    paddingHorizontal: 6,
    paddingVertical: 4,
    borderRadius: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  viewModeBtnActive: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 2,
  },
  listContent: {
    paddingBottom: Spacing.four,
    gap: Spacing.one,
    width: "100%",
  },
  gridListContent: {
    ...Platform.select({
      web: {
        display: "grid" as any,
        gap: 6,
        width: "100%",
      },
      default: {
        width: "100%",
      },
    }),
  },
  columnWrapper: {
    gap: 6,
    marginBottom: 4,
    width: "100%",
    justifyContent: "space-between",
  },
  chipFlowListContent: {
    width: "100%",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
  },
  flowListContent: {
    width: "100%",
  },
  flowColumnWrapper: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 3,
    marginBottom: 3,
    width: "100%",
  },
  flowItemWrapper: {
    width: "auto",
    flexGrow: 0,
    flexShrink: 0,
  },
  groupHeaderContainer: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 0,
    borderWidth: 1,
    marginTop: Spacing.one,
    marginBottom: 2,
  },
  groupHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.one,
  },
  groupHeaderTitle: {
    fontFamily: Fonts.serif,
    fontSize: 13,
    fontWeight: "700",
  },
  groupHeaderBadge: {
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 0,
    borderWidth: 1,
  },
  groupHeaderBadgeText: {
    fontSize: 9.5,
    fontWeight: "700",
  },
  singleColumnCell: {
    width: "100%",
    boxSizing: "border-box" as any,
  },
  fullWidthGridCell: {
    width: "100%",
    gridColumn: "1 / -1",
    boxSizing: "border-box",
  } as any,
  itemWrapper: {
    width: "100%",
    borderRadius: 0,
    alignItems: "stretch",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box" as any,
    position: "relative",
  } as any,
  itemHovered: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
    ...Platform.select({
      web: {
        transform: "translateY(-1px)",
      } as any,
      default: {},
    }),
  },
  emptyContainer: {
    width: "100%",
    alignSelf: "stretch",
    gridColumn: "1 / -1",
    boxSizing: "border-box",
    padding: Spacing.three,
    borderRadius: 0,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
    marginVertical: Spacing.two,
  } as any,
  emptyArchivalLabel: {
    fontSize: 10,
    letterSpacing: 1.5,
    marginBottom: 4,
  },
  emptyText: {
    fontSize: 13,
    textAlign: "center",
  },
  clearFilterBtn: {
    marginTop: Spacing.two,
    paddingHorizontal: Spacing.two,
    paddingVertical: 6,
    borderRadius: 6,
  },
  clearFilterText: {
    fontSize: 12,
    fontWeight: "600",
  },
  paginationFooter: {
    width: "100%",
    flexBasis: "100%",
    paddingVertical: Spacing.one,
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.6,
  },
  paginationText: {
    fontSize: 11,
    fontStyle: "italic",
  },
});
